/**
 * Vehicle recommendation service (Milestone 13).
 */
import { searchPublicVehicleListings } from "./publicVehicleListingsService";
import { shouldShowListingInSearch } from "../utils/automapListing/listingVisibility";
import { FAIR_PRICE_LABELS } from "../constants/automapFairPriceConstants";
import { shouldShowUnavailableDetailPage } from "../utils/automapListing/listingVisibility";
import { getDealerTrustViewModel } from "../utils/dealerTrustViewModel";
import { resolveRiskLevel } from "../utils/vehicleRiskViewModel";
import {
  RECOMMENDATION_CANDIDATE_LIMIT,
  RECOMMENDATION_DETAIL_COPY_ES,
  RECOMMENDATION_SECTION_COPY_ES,
  RECOMMENDATION_SECTION_KEYS,
} from "../constants/automapVehicleRecommendationConstants";
import { scoreSimilarListing, sortScoredListings } from "../utils/vehicleRecommendationScoring";
import {
  buildSearchHrefFromListing,
  getSimilarVehiclesSearchHref,
} from "../utils/vehicleRecommendationSearchHref";
import {
  getSafeDealerProfileHref,
  listingBelongsToDealer,
  normalizeDealerProfileFromSources,
} from "../utils/dealerProfile";
import { getDealerProfilePublicState } from "../utils/dealerProfile/dealerPublicState";

// String literals avoid init-order failures when this module loads inside a circular graph.
const SECTION_PRIORITY = [
  "compare_options",
  "same_dealer",
  "better_price",
  "similar_make_model",
  "similar_body_type",
  "similar_budget",
  "recently_added",
  "fallback_popular",
];

function listingKey(listing) {
  return listing?.id || listing?.slug || null;
}

function isEligibleCandidate(source, candidate) {
  if (!candidate?.id) return false;
  if (source?.id && candidate.id === source.id) return false;
  return shouldShowListingInSearch(candidate);
}

function priceWithinRatio(sourcePrice, candidatePrice, ratio) {
  if (sourcePrice == null || candidatePrice == null || sourcePrice <= 0) return false;
  const delta = Math.abs(Number(candidatePrice) - Number(sourcePrice)) / Number(sourcePrice);
  return delta <= ratio;
}

function yearWithin(sourceYear, candidateYear, years) {
  if (sourceYear == null || candidateYear == null) return false;
  return Math.abs(Number(sourceYear) - Number(candidateYear)) <= years;
}

function isBetterPricedAlternative(source, candidate) {
  const label = candidate.fairPriceLabel;
  if (label === FAIR_PRICE_LABELS.below_market || label === FAIR_PRICE_LABELS.fair_price) {
    return true;
  }
  if (source.priceCop != null && candidate.priceCop != null) {
    return Number(candidate.priceCop) < Number(source.priceCop) * 0.95;
  }
  return false;
}

function isSameMakeModel(source, candidate) {
  return (
    source.make &&
    source.model &&
    candidate.make === source.make &&
    candidate.model === source.model &&
    yearWithin(source.year, candidate.year, 3) &&
    priceWithinRatio(source.priceCop, candidate.priceCop, 0.25)
  );
}

function isSameBodyType(source, candidate) {
  return (
    source.bodyType &&
    candidate.bodyType === source.bodyType &&
    priceWithinRatio(source.priceCop, candidate.priceCop, 0.3)
  );
}

function isSimilarBudget(source, candidate) {
  return priceWithinRatio(source.priceCop, candidate.priceCop, 0.25);
}

function isRecentlyAdded(candidate) {
  const ts = Date.parse(candidate.updatedAt || candidate.importedAt || candidate.lastConfirmedAt || "");
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= 21 * 24 * 60 * 60 * 1000;
}

function shouldPrioritizeCompare(source, dealerVm) {
  const label = source.fairPriceLabel;
  const risk = resolveRiskLevel(source);
  return (
    shouldShowUnavailableDetailPage(source) ||
    [FAIR_PRICE_LABELS.high_price, FAIR_PRICE_LABELS.slightly_high].includes(label) ||
    ["medium", "high"].includes(risk) ||
    (dealerVm && !dealerVm.canContact)
  );
}

function resolveCatalogLabel(t, key, fallback) {
  if (!t) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

function buildSection(key, listings, sourceListing, options = {}) {
  const meta = RECOMMENDATION_SECTION_COPY_ES[key] || {};
  const slug = options.slug || "automap";
  const { t = null } = options;
  let ctaHref = getSimilarVehiclesSearchHref(sourceListing, { slug, sectionKey: key });
  let ctaLabel = t
    ? t("automotive.detail.recommendations.viewMore")
    : RECOMMENDATION_DETAIL_COPY_ES.viewMore;
  let title = meta.title || RECOMMENDATION_DETAIL_COPY_ES.heading;
  let subtitle = meta.subtitle || RECOMMENDATION_DETAIL_COPY_ES.subheading;

  if (t) {
    const sectionBase = `automotive.detail.recommendationSections.${key}`;
    title = resolveCatalogLabel(t, `${sectionBase}.title`, title);
    subtitle = resolveCatalogLabel(t, `${sectionBase}.subtitle`, subtitle);
    ctaLabel = resolveCatalogLabel(
      t,
      key === RECOMMENDATION_SECTION_KEYS.SAME_DEALER
        ? "automotive.detail.dealerTrust.viewDealerProfile"
        : `${sectionBase}.ctaLabel`,
      ctaLabel
    );
  } else if (key === RECOMMENDATION_SECTION_KEYS.SAME_DEALER && sourceListing.dealerSlug) {
    ctaLabel = meta.ctaLabel || "Ver perfil del concesionario";
  }

  if (key === RECOMMENDATION_SECTION_KEYS.SAME_DEALER && sourceListing.dealerSlug) {
    const dealerProfile = normalizeDealerProfileFromSources({
      listing: sourceListing,
      rawDealer: options.dealer || null,
    });
    ctaHref = getSafeDealerProfileHref(dealerProfile, { slug, usePublicRoute: true }) || ctaHref;
  }

  return {
    key,
    title,
    subtitle,
    reason: meta.reason || key,
    listings,
    ctaLabel,
    ctaHref,
    priority: SECTION_PRIORITY.indexOf(key),
  };
}

function assignSections(sourceListing, scoredCandidates, options = {}) {
  const used = new Set();
  const dealerProfile = normalizeDealerProfileFromSources({
    listing: sourceListing,
    rawDealer: options.dealer || null,
  });
  const dealerPublicState = getDealerProfilePublicState(dealerProfile);
  const dealerVm = getDealerTrustViewModel(sourceListing, options.dealer || null, {
    slug: options.slug,
  });
  const sections = [];
  const maxPerSection = options.maxPerSection || RECOMMENDATION_DETAIL_COPY_ES.maxListingsPerSection;

  const take = (key, predicate, minCount = 1, limit = maxPerSection) => {
    const selected = [];
    for (const entry of scoredCandidates) {
      const keyId = listingKey(entry.listing);
      if (!keyId || used.has(keyId)) continue;
      if (!predicate(entry.listing, entry.score)) continue;
      used.add(keyId);
      selected.push(entry.listing);
      if (selected.length >= limit) break;
    }
    if (selected.length >= minCount) {
      sections.push(buildSection(key, selected, sourceListing, options));
    }
  };

  if (shouldPrioritizeCompare(sourceListing, dealerVm)) {
    take(
      RECOMMENDATION_SECTION_KEYS.COMPARE_OPTIONS,
      (candidate) =>
        isBetterPricedAlternative(sourceListing, candidate) ||
        isSameMakeModel(sourceListing, candidate) ||
        isSameBodyType(sourceListing, candidate),
      1,
      maxPerSection
    );
  }

  if (
    dealerPublicState.canShowProfile &&
    dealerPublicState.canShowInventory &&
    sourceListing.dealerId &&
    dealerVm.canContact !== false
  ) {
    take(
      RECOMMENDATION_SECTION_KEYS.SAME_DEALER,
      (candidate) => listingBelongsToDealer(candidate, dealerProfile),
      2,
      RECOMMENDATION_DETAIL_COPY_ES.maxListingsCompact
    );
  }

  if (
    [FAIR_PRICE_LABELS.high_price, FAIR_PRICE_LABELS.slightly_high].includes(sourceListing.fairPriceLabel) ||
    ["medium", "high"].includes(resolveRiskLevel(sourceListing))
  ) {
    take(
      RECOMMENDATION_SECTION_KEYS.BETTER_PRICE,
      (candidate) => isBetterPricedAlternative(sourceListing, candidate),
      1,
      maxPerSection
    );
  }

  take(
    RECOMMENDATION_SECTION_KEYS.SIMILAR_MAKE_MODEL,
    (candidate) => isSameMakeModel(sourceListing, candidate),
    1,
    maxPerSection
  );

  take(
    RECOMMENDATION_SECTION_KEYS.SIMILAR_BODY_TYPE,
    (candidate) => isSameBodyType(sourceListing, candidate),
    1,
    maxPerSection
  );

  take(
    RECOMMENDATION_SECTION_KEYS.SIMILAR_BUDGET,
    (candidate) => isSimilarBudget(sourceListing, candidate),
    1,
    maxPerSection
  );

  take(
    RECOMMENDATION_SECTION_KEYS.RECENTLY_ADDED,
    (candidate) => isRecentlyAdded(candidate),
    1,
    maxPerSection
  );

  take(
    RECOMMENDATION_SECTION_KEYS.FALLBACK_POPULAR,
    () => true,
    1,
    maxPerSection
  );

  return sections.sort((a, b) => a.priority - b.priority);
}

async function fetchCandidateBatch(slug, filters, excludeId, limit, profileId) {
  if (limit <= 0) return [];
  const result = await searchPublicVehicleListings(
    slug,
    { ...filters, page: 1, pageSize: Math.min(limit + 4, 24) },
    { profileId, allowDevFallback: true }
  );
  if (!result.ok) return [];
  return result.listings.filter((item) => isEligibleCandidate({ id: excludeId }, item));
}

async function fetchRecommendationCandidates(slug, listing, options = {}) {
  if (!listing?.id) return [];

  const excludeId = listing.id;
  const profileId = options.profileId;
  const limit = options.candidateLimit || RECOMMENDATION_CANDIDATE_LIMIT;
  const strategies = [];

  if (listing.make && listing.model) {
    strategies.push({
      make: listing.make,
      model: listing.model,
      city: listing.city || undefined,
      yearMin: listing.year ? Number(listing.year) - 3 : undefined,
      yearMax: listing.year ? Number(listing.year) + 3 : undefined,
      priceMinCop: listing.priceCop != null ? Math.round(Number(listing.priceCop) * 0.75) : undefined,
      priceMaxCop: listing.priceCop != null ? Math.round(Number(listing.priceCop) * 1.25) : undefined,
      sort: "best_value",
    });
  }

  if (listing.bodyType && listing.city) {
    strategies.push({
      bodyType: listing.bodyType,
      city: listing.city,
      priceMinCop: listing.priceCop != null ? Math.round(Number(listing.priceCop) * 0.7) : undefined,
      priceMaxCop: listing.priceCop != null ? Math.round(Number(listing.priceCop) * 1.3) : undefined,
      sort: "best_value",
    });
  }

  if (listing.city && listing.priceCop != null) {
    strategies.push({
      city: listing.city,
      priceMinCop: Math.round(Number(listing.priceCop) * 0.6),
      priceMaxCop: Math.round(Number(listing.priceCop) * 1.4),
      sort: "best_value",
    });
  }

  if (listing.dealerId) {
    strategies.push({ keyword: listing.dealerName || listing.make, sort: "newest" });
  }

  if (listing.city) {
    strategies.push({ city: listing.city, sort: "newest" });
  }

  strategies.push({ sort: "recommended" });

  const seen = new Set([excludeId]);
  const collected = [];

  const batches = await Promise.all(
    strategies.map((filters) =>
      fetchCandidateBatch(slug, filters, excludeId, limit, profileId)
    )
  );

  batches.forEach((batch) => {
    batch.forEach((item) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      collected.push(item);
    });
  });

  return collected.slice(0, limit);
}

export async function getVehicleRecommendationsForListing(slug, listing, options = {}) {
  if (!listing?.id) {
    return { sections: [], allRecommendations: [], warnings: ["missing_listing"] };
  }

  try {
    const candidates = await fetchRecommendationCandidates(slug, listing, options);
    const scored = sortScoredListings(
      candidates
        .map((candidate) => ({
          listing: candidate,
          score: scoreSimilarListing(listing, candidate),
        }))
        .filter((entry) => entry.score > -50)
    );

    const sections = assignSections(listing, scored, options).slice(
      0,
      options.maxSections || RECOMMENDATION_DETAIL_COPY_ES.maxSectionsOnDetail
    );

    const allRecommendations = [];
    const seen = new Set();
    sections.forEach((section) => {
      section.listings.forEach((item) => {
        const key = listingKey(item);
        if (!key || seen.has(key)) return;
        seen.add(key);
        allRecommendations.push(item);
      });
    });

    if (!allRecommendations.length && scored.length) {
      const fallback = scored.slice(0, options.limit || 8).map((entry) => entry.listing);
      sections.push(
        buildSection(RECOMMENDATION_SECTION_KEYS.FALLBACK_POPULAR, fallback, listing, options)
      );
      fallback.forEach((item) => allRecommendations.push(item));
    }

    return {
      sections,
      allRecommendations,
      searchHref: buildSearchHrefFromListing(listing, { slug: options.slug }),
      warnings: candidates.length ? [] : ["no_candidates"],
    };
  } catch {
    return {
      sections: [],
      allRecommendations: [],
      searchHref: buildSearchHrefFromListing(listing, { slug: options.slug }),
      warnings: ["fetch_failed"],
    };
  }
}

/**
 * Flat similar listings (backward compatible with Milestone 7).
 */
export async function getSimilarVehicleListings(slug, listing, options = {}) {
  const result = await getVehicleRecommendationsForListing(slug, listing, options);
  const limit = options.limit ?? 8;
  return result.allRecommendations.slice(0, limit);
}

export async function searchSimilarPublicListings(slug, listing, options = {}) {
  return getVehicleRecommendationsForListing(slug, listing, options);
}

export function dedupeRecommendationListings(sections = []) {
  const seen = new Set();
  const dedupedSections = sections.map((section) => {
    const listings = section.listings.filter((listing) => {
      const key = listingKey(listing);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return { ...section, listings };
  });
  return dedupedSections.filter((section) => section.listings.length > 0);
}
